import { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import type { SingularDayCategory } from '@ebalance/proto/bffuser/v1/singular_day_model_pb'

import { AREA_CODE_OPTIONS } from '~/models/area'
import {
  defineFilterCodec,
  integer,
  oneOf,
  optional,
} from '~/models/filterCodec'
import { SINGULAR_DAY_CATEGORY_OPTIONS } from '~/pages/AdminSingularDay/models/options'

/**
 * 特異日マスタのフィルタバー state (in-memory 形状)。画面遷移・再訪をまたいで
 * 保持する対象 (AC-SC-031 [P0])。
 *
 * 対象は FilterToolbar 上の 3 フィルタ (年度 / エリア / カテゴリ) で、いずれも
 * `undefined` = 絞り込みなし。
 *
 * ## 保持しないもの
 *
 * モーダル状態 (`modalState`)・削除エラー・情報メッセージは、フィルタではなく
 * 一時的な UI 状態。再訪時に復元すると「閉じたはずのダイアログが開く」ため含めない。
 */
export type AdminSingularDayFilters = {
  fiscalYear: number | undefined
  areaFilter: AreaCode | undefined
  categoryFilter: SingularDayCategory | undefined
}

/** フィルタ既定値 = 3 つとも絞り込みなし。時計に依存しない。 */
export function defaultAdminSingularDayFilters(): AdminSingularDayFilters {
  return {
    fiscalYear: undefined,
    areaFilter: undefined,
    categoryFilter: undefined,
  }
}

/**
 * 保存形式は `{ fiscalYear?, areaFilter?, categoryFilter? }` (`undefined` はキーごと
 * 落ち、復元側は「キーが無い」を「絞り込みなし」と読む)。壊れた値・stale enum・
 * 未知フィールドは項目単位で既定へ落ちる。
 *
 * ## 年度だけ候補表で検証しない
 *
 * 年度の選択肢は**登録済みデータから導出**される (`fiscalYearOptionsFromDates`)
 * ので、ここに固定の候補集合が無い。整数であることだけ確かめ、候補から消えた年度は
 * 画面側の effect が「すべて」へ戻す (= AC-MSD-001 の既存挙動)。
 */
export const adminSingularDayFiltersCodec =
  defineFilterCodec<AdminSingularDayFilters>({
    defaults: defaultAdminSingularDayFilters,
    fields: {
      fiscalYear: optional(integer()),
      areaFilter: optional(oneOf<AreaCode>(AREA_CODE_OPTIONS)),
      categoryFilter: optional(
        oneOf<SingularDayCategory>(SINGULAR_DAY_CATEGORY_OPTIONS),
      ),
    },
  })
